import { useEffect, useRef, useState } from 'react';
import { RotateCcw, Trash2, ChevronDown, X, Quote, Copy, Check } from 'lucide-react';
import { useFlag, getFlag } from '../../lib/flags.js';
import { flipIn, flipOut } from '../../lib/flip.js';
import { splitSegments, parseQuoted, extractReactions } from './chatUtils.js';
import Avatar from '../../components/Avatar.jsx';
import { useMusic } from '../musicbox/MusicContext.jsx';

/* ====================== 单条消息 ====================== */
// 复制到剪贴板：优先用现代 Clipboard API（https 下 iOS 也支持），
// 老 webview 没有就退回临时 textarea + execCommand。
function copyText(text) {
  const t = (text || '').trim();
  if (!t) return Promise.resolve();
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(t).catch(() => fallbackCopy(t));
  }
  fallbackCopy(t);
  return Promise.resolve();
}
function fallbackCopy(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  } catch {}
}

// 把 db 里的 content 解析成 {text, images, journey, music}
// journey = 云旅行明信片卡片块（2026-08-31）：{type:'journey', url, from, title, sub, tagline, covers[]}
// music = 聊天里的音乐卡（2026-09-03）：{type:'music', id, name, artist, cover} —— 克克在对话里递的歌
// voice = 他的语音条（2026-10-08）：{type:'voice', id, text(英文原话), zh(他写的中文意思), dur(秒)}
function parseContent(raw) {
  if (raw == null) return { text: '', images: [], journey: null, music: null, voice: null };
  if (typeof raw !== 'string') return { text: String(raw), images: [], journey: null, music: null, voice: null };
  const trimmed = raw.trim();
  if (!trimmed.startsWith('[')) return { text: raw, images: [], journey: null, music: null, voice: null };
  try {
    const arr = JSON.parse(trimmed);
    if (!Array.isArray(arr)) return { text: raw, images: [], journey: null, music: null, voice: null };
    let text = '';
    const images = [];
    let journey = null;
    let music = null;
    let voice = null;
    for (const b of arr) {
      if (!b) continue;
      if (b.type === 'text' && b.vt) {
        // 语音条附带的文字（给大脑读的），界面上不当正文显示
      } else if (b.type === 'text' && typeof b.text === 'string') {
        text = text ? text + '\n' + b.text : b.text;
      } else if (b.type === 'image' && b.source?.data) {
        images.push({
          media_type: b.source.media_type || 'image/jpeg',
          data: b.source.data,
        });
      } else if (b.type === 'journey' && typeof b.url === 'string') {
        journey = {
          url: b.url,
          from: typeof b.from === 'string' ? b.from : '',
          title: typeof b.title === 'string' ? b.title : '',
          sub: typeof b.sub === 'string' ? b.sub : '',
          tagline: typeof b.tagline === 'string' ? b.tagline : '',
          covers: Array.isArray(b.covers) ? b.covers.filter((c) => typeof c === 'string').slice(0, 6) : [],
          places: Array.isArray(b.places) ? b.places.map((p) => (typeof p === 'string' ? p : '')).slice(0, 6) : [],
        };
      } else if (b.type === 'voice' && typeof b.id === 'string') {
        voice = {
          id: b.id,
          text: typeof b.text === 'string' ? b.text : '',
          zh: typeof b.zh === 'string' ? b.zh : '',
          dur: typeof b.dur === 'number' ? b.dur : 0,
          mine: !!b.mine,
          tone: typeof b.tone === 'string' ? b.tone : '',
        };
      } else if (b.type === 'music' && b.id != null) {
        music = {
          id: String(b.id),
          name: typeof b.name === 'string' ? b.name : '',
          artist: typeof b.artist === 'string' ? b.artist : '',
          cover: typeof b.cover === 'string' ? b.cover : '',
        };
      }
    }
    return { text, images, journey, music, voice };
  } catch {
    return { text: raw, images: [], journey: null, music: null, voice: null };
  }
}

/* 他的语音条（2026-10-08 铃点单）：点一下听他说话；长按出「转文字」（英文原话）/「翻译」（中文）。
   音频走登录墙后的 /api/voice/file。同一时刻只响一条；他开口时音乐盒先停一下。 */
let curVoiceAudio = null;
function voiceBars(id) {
  // 用 id 算一串固定高矮的竖条，每条语音长得不一样、但刷新也不变
  let h = 0;
  const out = [];
  for (let i = 0; i < 18; i++) {
    h = (h * 31 + id.charCodeAt(i % id.length) + i * 7) % 997;
    out.push(0.32 + (h % 68) / 100);
  }
  return out;
}
function fmtVoiceDur(sec) {
  const s = Math.max(1, Math.round(sec || 0));
  return s < 60 ? s + '″' : Math.floor(s / 60) + '′' + (s % 60) + '″';
}
function VoiceBubble({ voice, mine = false }) {
  const mx = useMusic();
  const audioRef = useRef(null);
  const wrapRef = useRef(null);
  const pressTimer = useRef(0);
  const longFired = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [prog, setProg] = useState(0);
  const [menu, setMenu] = useState(false);
  const [show, setShow] = useState(''); // '' | 'text' | 'zh'
  const [zh, setZh] = useState(voice.zh || '');
  const [zhLoading, setZhLoading] = useState(false);
  const bars = useRef(voiceBars(voice.id)).current;

  useEffect(() => () => {
    clearTimeout(pressTimer.current);
    const a = audioRef.current;
    if (a) { a.pause(); if (curVoiceAudio === a) curVoiceAudio = null; }
  }, []);

  // 菜单开着时，点别处就收起
  useEffect(() => {
    if (!menu) return;
    const off = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener('pointerdown', off);
    return () => document.removeEventListener('pointerdown', off);
  }, [menu]);

  const toggle = () => {
    let a = audioRef.current;
    if (!a) {
      a = new Audio('/api/voice/file?id=' + encodeURIComponent(voice.id));
      a.preload = 'auto';
      a.addEventListener('play', () => setPlaying(true));
      a.addEventListener('pause', () => setPlaying(false));
      a.addEventListener('ended', () => { setPlaying(false); setProg(0); });
      a.addEventListener('timeupdate', () => setProg(a.duration ? a.currentTime / a.duration : 0));
      audioRef.current = a;
    }
    if (a.paused) {
      if (curVoiceAudio && curVoiceAudio !== a) curVoiceAudio.pause();
      curVoiceAudio = a;
      if (mx?.playing) mx.toggle();
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  };

  const pressStart = () => {
    longFired.current = false;
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      longFired.current = true;
      setMenu(true);
      try { navigator.vibrate?.(10); } catch {}
    }, 450);
  };
  const pressEnd = () => clearTimeout(pressTimer.current);
  const onTap = () => {
    if (longFired.current) { longFired.current = false; return; }
    if (menu) { setMenu(false); return; }
    toggle();
  };

  const showText = () => { setMenu(false); setShow((v) => (v === 'text' ? '' : 'text')); };
  const showZh = async () => {
    setMenu(false);
    setShow((v) => (v === 'zh' ? '' : 'zh'));
    if (zh || zhLoading) return;
    setZhLoading(true);
    try {
      const r = await fetch('/api/voice/translate', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: voice.id, text: voice.text }),
      });
      const d = await r.json();
      if (d && d.zh) setZh(d.zh);
    } catch {}
    setZhLoading(false);
  };

  const width = 104 + (Math.min(voice.dur || 0, 40) / 40) * 120;
  return (
    <div className="ch-voice-wrap" ref={wrapRef}>
      {menu && (
        <div className="ch-voice-menu" role="menu">
          <button type="button" role="menuitem" onClick={showText}>{show === 'text' ? '收起原文' : '转文字'}</button>
          {!mine && <button type="button" role="menuitem" onClick={showZh}>{show === 'zh' ? '收起翻译' : '翻译'}</button>}
        </div>
      )}
      <button
        type="button"
        className={'ch-voice' + (mine ? ' ch-bubble ch-voice-mine' : '') + (playing ? ' playing' : '')}
        style={{ width }}
        onClick={onTap}
        onPointerDown={pressStart}
        onPointerUp={pressEnd}
        onPointerLeave={pressEnd}
        onPointerCancel={pressEnd}
        onContextMenu={(e) => e.preventDefault()}
        aria-label={playing ? '暂停语音' : (mine ? '听你的语音' : '听他的语音')}
      >
        <span className="ch-voice-ic" aria-hidden="true">
          {playing
            ? <svg viewBox="0 0 24 24" width="15" height="15"><rect x="6" y="5" width="4" height="14" rx="1.4" fill="currentColor" /><rect x="14" y="5" width="4" height="14" rx="1.4" fill="currentColor" /></svg>
            : <svg viewBox="0 0 24 24" width="15" height="15"><path d="M8 5 L8 19 L19 12 Z" fill="currentColor" /></svg>}
        </span>
        <span className="ch-voice-bars" aria-hidden="true">
          {bars.map((h, i) => (
            <i key={i} className={i / bars.length < prog ? 'on' : ''} style={{ height: Math.round(h * 100) + '%' }} />
          ))}
        </span>
        <span className="ch-voice-dur">{fmtVoiceDur(voice.dur)}</span>
      </button>
      {show === 'text' && (
        <div className="ch-voice-text" onClick={() => setShow('')}>
          {voice.text || '（没听清说了什么）'}
          {mine && voice.tone && <div className="ch-voice-tone">听起来：{voice.tone}</div>}
        </div>
      )}
      {show === 'zh' && (
        <div className="ch-voice-text zh" onClick={() => setShow('')}>
          {zh || (zhLoading ? '翻译中…' : '这条暂时翻不出来，等会儿再长按试试～')}
        </div>
      )}
    </div>
  );
}

/* 聊天里的音乐卡（2026-09-03 铃点单）：克克在对话里递你一首歌——封面+歌名+歌手，
   点一下就进常驻播放器整首放（同时也已进音乐盒收藏架）。字体用系统苹方（在聊天界面里）。 */
function MusicCard({ music }) {
  const mx = useMusic();
  const cover = music.cover ? music.cover + '?param=160y160' : '';
  const onTap = () => {
    if (!mx) return;
    mx.play([{ id: music.id, name: music.name, artist: music.artist, cover: music.cover }], 0);
    mx.setShowLyrics?.(true);
  };
  return (
    <button type="button" className="ch-music" onClick={onTap} title="点开听整首">
      <span
        className="ch-music-cover"
        style={cover ? { backgroundImage: `url("${cover}")` } : undefined}
      >
        {!cover && <span className="ch-music-note">♪</span>}
        <span className="ch-music-play" aria-hidden="true">▶</span>
      </span>
      <span className="ch-music-meta">
        <span className="ch-music-nm">{music.name || '一首歌'}</span>
        <span className="ch-music-ar">{music.artist}</span>
      </span>
    </button>
  );
}

/* 云旅行明信片卡片（2026-08-31 铃亲手拖定的定稿数值，样式在 Chat.css .ch-jc-*）：
   冷白纸卡落在夜空聊天里。互动照原作者：手指在六张图上滑动，滑到哪张哪张就弹起放大、
   露出那一站的地名（原图右侧那张效果）；快点一下 → 由 Chat 的浮层铺全屏翻卡。 */
// 程序坞式波浪放大 + 焦点自动居中（2026-08-31 铃点单）：
// 手指滑到哪张，那张就放最大、邻居递减放大（宽高一起变），整排还自动横滚、把它送到正中。
// 「离焦点第几张」查两张表：宽度乘数(×静息基准宽) + 高度(px)。静息 -1 = 全部还原、滚回起点。
const JC_WMUL = [2.15, 1.5, 1.12, 0.78]; // d=0,1,2,>=3 的宽度乘数(落差加大)
// 高度都 ≤ .ch-jc-strip 的固定高(236)，底部对齐向上长；落差拉大=更像原作者的波峰
const JC_H = [236, 204, 182, 160]; // d=0,1,2,>=3 的高度(px)
const JC_GAP = 7; // 与 .ch-jc-strip 的 gap 保持一致（算居中位置要用）
function jcStep(d) {
  return d >= JC_WMUL.length ? JC_WMUL.length - 1 : d;
}

function JourneyCard({ journey, onOpen }) {
  const covers = journey.covers && journey.covers.length ? journey.covers : [];
  const places = journey.places || [];
  const n = covers.length;
  const [focus, setFocus] = useState(-1); // 手指正对着第几张（-1=静息）
  const [vpW, setVpW] = useState(0); // 胶片视口宽（算每张宽度 + 居中平移都要它）
  const stripRef = useRef(null);
  const clearTimer = useRef(null);

  // 静息时每张的基准宽 = 六张正好铺满视口
  const baseW = vpW > 0 && n > 0 ? (vpW - JC_GAP * (n - 1)) / n : 0;
  const wMul = (d) => JC_WMUL[jcStep(d)];
  const hFor = (d) => JC_H[jcStep(d)];
  const widthOf = (i) => (focus < 0 ? baseW : baseW * wMul(Math.abs(i - focus)));

  // 量视口宽（挂载 + 尺寸变化）
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const measure = () => setVpW(el.clientWidth);
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => { if (ro) ro.disconnect(); };
  }, []);

  // 焦点那张要平移到正中：位置全用基准宽算（不读正在过渡的实时宽），静息回 0。
  let trackX = 0;
  if (baseW > 0 && focus >= 0) {
    let left = 0;
    for (let i = 0; i < focus; i++) left += widthOf(i) + JC_GAP;
    const center = left + widthOf(focus) / 2;
    trackX = vpW / 2 - center;
  }

  // ★关键：用手指在这排上的左右位置直接算第几张（像拉滑块），跟图有没有移动无关
  //   → 不再靠 elementFromPoint 读移动中的 DOM，彻底断掉「选中→平移→又选中别的」的死循环（地震根源）。
  const idxAt = (clientX) => {
    const el = stripRef.current;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    let rel = (clientX - rect.left) / rect.width;
    rel = Math.max(0, Math.min(1, rel));
    const idx = Math.round(rel * (n - 1));
    clearTimeout(clearTimer.current);
    setFocus((prev) => (prev === idx ? prev : idx)); // 同一张不重复 setState
  };
  const onTouchMove = (e) => {
    const t = e.touches && e.touches[0];
    if (t) idxAt(t.clientX);
  };
  const endWave = () => {
    clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => setFocus(-1), 340);
  };

  return (
    <div
      className="ch-jc"
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(journey.url)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(journey.url); }
      }}
      title="点开走一遍"
    >
      <div className="ch-jc-kicker">JOURNEYS</div>
      <div className="ch-jc-ttl">
        {journey.from ? <span>{journey.from} </span> : null}
        <b>{journey.title}</b>
      </div>
      {journey.sub && <div className="ch-jc-sub">{journey.sub}</div>}
      {covers.length > 0 && (
        <div
          className="ch-jc-strip"
          ref={stripRef}
          onTouchStart={onTouchMove}
          onTouchMove={onTouchMove}
          onTouchEnd={endWave}
          onTouchCancel={endWave}
          onMouseMove={(e) => idxAt(e.clientX)}
          onMouseLeave={() => setFocus(-1)}
        >
          <div className="ch-jc-track" style={{ transform: `translateX(${trackX}px)` }}>
            {covers.map((c, i) => {
              const d = focus < 0 ? 99 : Math.abs(i - focus);
              const h = focus < 0 ? 189 : hFor(d);
              const cls =
                'ch-jc-s' + (d === 0 ? ' is-mag' : '') + (d === 1 ? ' is-near' : '');
              const style = { backgroundImage: `url(${c})`, height: h + 'px' };
              if (baseW > 0) {
                style.flex = '0 0 auto';
                style.width = widthOf(i) + 'px';
              }
              return (
                <div key={i} data-idx={i} className={cls} style={style}>
                  {places[i] ? <span className="ch-jc-cap">{places[i]}</span> : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {journey.tagline && <div className="ch-jc-tag">{journey.tagline}</div>}
    </div>
  );
}

// 把消息文本切成段落渲染：
// Sael爱用空行(\n\n)分隔每句，原样渲染会顶出整行空白、稀稀拉拉。
// 这里按"空行"切段，每段一个 <p>（小段间距）；段内单换行用 pre-wrap 保留但紧凑。
// trailing：可选的尾随节点（TG 时间戳）——塞进最后一个段落内部，靠 float 贴到最后一行行尾，
// 最后一行没占满就跟在行尾、占满了才自己掉下一行（2026-08-16 铃点单，避免白多一行、气泡显高）。
function renderMessageBody(text, trailing) {
  const paras = String(text || '')
    .split(/\n\s*\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (paras.length === 0) return null;
  const lastIdx = paras.length - 1;
  return paras.map((p, i) => (
    <p key={i} className="ch-para">
      {p}
      {trailing && i === lastIdx ? trailing : null}
    </p>
  ));
}

/* 应用内图片灯箱（2026-07-02）：替代 window.open —— iOS standalone PWA 会拦
   新窗口导致点图没反应，而且应用内浮层本来就更好看。点任意处/Esc 关闭。
   2026-07-19 动效五件套②：FLIP —— 传入 fromRect（被点的缩略图位置）时，
   大图从缩略图原地长大；关闭时缩回原位再卸载。没有 fromRect / 开了减弱动效
   则回退旧的淡入。 */
export function ImageLightbox({ img, fromRect, onClose }) {
  const imgRef = useRef(null);
  const boxRef = useRef(null);
  const closingRef = useRef(false);

  const flip = !!fromRect && !getFlag('reduce_motion');

  // 关闭：先把大图缩回缩略图的位置，动画走完再真正卸载
  const requestClose = () => {
    if (closingRef.current) return;
    const el = imgRef.current;
    if (!flip || !el) {
      onClose();
      return;
    }
    closingRef.current = true;
    boxRef.current?.classList.add('ch-lightbox-closing');
    flipOut(el, fromRect, onClose);
  };

  useEffect(() => {
    if (!img) return;
    closingRef.current = false;
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img]);

  // 打开：等大图有了最终位置，从 fromRect 原地长大（数学在 lib/flip.js）
  useEffect(() => {
    if (!img || !flip) return;
    flipIn(imgRef.current, fromRect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, flip]);

  if (!img) return null;
  return (
    <div
      ref={boxRef}
      className={'ch-lightbox' + (flip ? ' ch-lightbox-flip' : '')}
      onClick={requestClose}
      role="dialog"
      aria-label="图片预览"
    >
      <button className="ch-lightbox-close" onClick={requestClose} aria-label="关闭">
        <X size={18} strokeWidth={1.8} />
      </button>
      <img
        ref={imgRef}
        src={`data:${img.media_type};base64,${img.data}`}
        alt="图片预览"
        className="ch-lightbox-img"
      />
    </div>
  );
}

// 消息角落的小时间（2026-07-11 铃提）：她设备的本地时区，HH:MM
function fmtTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const TOOL_PHASE_LABEL = {
  'tool:list_recent_memories': 'Sael正在翻最近的桶……',
  'tool:search_memories': 'Sael正在搜记忆库……',
  'tool:read_memory': 'Sael正在细读那一桶……',
  'tool:write_memory': 'Sael正在为你写下……',
};
const TOOL_DONE_LABEL = {
  list_recent_memories: '翻',
  search_memories: '搜',
  read_memory: '读',
  write_memory: '写',
};

// 单张工具卡片：平时收着一行（图标 + 名字 [+ 翻了N条]），
// 记忆工具带 ▾，点一下展开它翻到的记忆标题；其它工具没有可展开内容就不带箭头。
function ToolCard({ t }) {
  const [open, setOpen] = useState(false);
  const bare = (t.name || '').split('__').pop();
  const mems = Array.isArray(t.memories) ? t.memories : [];
  const hasMem = mems.length > 0;
  // 铃点单(2026-09-02):直接显示工具的英文原名(breath / ToolSearch …)，
  // 比中文「翻记忆/取工具」更技术感、更高级；工具本来就是英文名。
  const title = bare || '工具';
  const err = t.ok === false;
  return (
    <div className={'ch-tool-card' + (err ? ' ch-tool-card-err' : '') + (open ? ' open' : '')}>
      <button
        type="button"
        className="ch-tool-card-head"
        onClick={() => hasMem && setOpen((o) => !o)}
        aria-expanded={hasMem ? open : undefined}
        style={hasMem ? undefined : { cursor: 'default' }}
      >
        <span className="ch-tool-card-icon" aria-hidden="true">✦</span>
        <span className="ch-tool-card-name">{title}</span>
        {hasMem && <span className="ch-tool-card-count">翻了 {mems.length} 条记忆</span>}
        {err && <span className="ch-tool-card-count ch-tool-card-count-err">似乎出错了</span>}
        {hasMem && <ChevronDown size={15} className="ch-tool-card-chev" aria-hidden="true" />}
      </button>
      {hasMem && open && (
        <ul className="ch-tool-card-body">
          {mems.map((mm, i) => (
            <li key={i} className="ch-mem-row">
              <span className="ch-mem-icon" aria-hidden="true">{mm.icon || '💭'}</span>
              <span className="ch-mem-title">{mm.title}</span>
              {mm.when && <span className="ch-mem-when">{String(mm.when).slice(5)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// 无框思考链（2026-07-11 铃定）：不套卡片、不写「Sael的思考」标签，
// 收起 = 直接露一行思考原文（省略号截断），点一下展开全文。
function ThinkingBox({ text, live }) {
  const [open, setOpen] = useState(false);
  if (!text || !text.trim()) return null;
  return (
    <button
      type="button"
      className={
        'ch-think' + (open ? ' ch-think-open' : '') + (live ? ' ch-think-live' : '')
      }
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      title={open ? '收起思考' : '展开思考'}
    >
      <ChevronDown size={12} strokeWidth={1.8} className="ch-think-chev" />
      <span className="ch-think-text">{text.trim()}</span>
    </button>
  );
}

export default function Message({ m, onDelete, onRegenerate, onRetry, generating, onImageView, onJourneyOpen, onQuote, reactions, justSent }) {
  const isUser = m.role === 'user';
  // ⑤ 显示思考卡片开关（默认开 = 跟以前一样自动展示；关 = 纯净对话）
  const [showThinking] = useFlag('thinking_card');
  // TG 三件套 ①：[split] 拆条小气泡（关掉 = 标记吞掉、并回一条）
  const [splitOn] = useFlag('split_bubbles');
  // 复制反馈：点一下变 ✓「已复制」，1.5s 后复原
  const [copied, setCopied] = useState(false);

  if (m.pending) {
    const phaseLabel = (m.phase && TOOL_PHASE_LABEL[m.phase]) || 'Sael正在想……';
    const trail = m.toolTrail || [];
    return (
      <div className="ch-msg ch-msg-assistant ch-msg-pending">
        {/* 思考链 + 工具痕迹 → 单独一行（隐形头像占位对齐，和最终消息一致） */}
        {((showThinking && m.thinking) || trail.length > 0) && (
          <div className="ch-msg-line ch-msg-line-head">
            <Avatar who="sael" className="ch-avatar ch-avatar-ghost" />
            <div className="ch-msg-col">
              {showThinking && m.thinking && <ThinkingBox text={m.thinking} live />}
              {trail.length > 0 && (
                <div className="ch-tool-trail">
                  {trail.map((t, i) => (
                    <ToolCard key={i} t={t} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="ch-msg-line">
          <Avatar who="sael" className="ch-avatar" />
          <div className="ch-msg-col">
            <div className="ch-bubble ch-bubble-pending">
              <span className="ch-pulse-dot" />
              <span className="ch-pulse-dot" />
              <span className="ch-pulse-dot" />
              <span className="ch-pending-text">{phaseLabel}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (m.error) {
    return (
      <div className="ch-msg ch-msg-assistant ch-msg-error">
        <span className="ch-avatar ch-avatar-error" aria-hidden="true">✦</span>
        <div className="ch-bubble ch-bubble-error">
          <div className="ch-error-text">
            Sael没接住这一句 ——
            <div className="ch-error-detail">{m.error}</div>
          </div>
          <div className="ch-msg-tools">
            <button
              className="ch-msg-tool"
              onClick={onRetry}
              disabled={generating}
            >
              <RotateCcw size={11} strokeWidth={1.6} /> 再试一次
            </button>
            <button className="ch-msg-tool" onClick={onDelete}>
              <Trash2 size={11} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { text, images, journey, music, voice } = parseContent(m.content);

  /* TG 三件套的文本预处理：
     - 铃的消息：开头可能有 [quote …] 引用标记 → 摘出来渲染成小引用条
     - Sael的消息：任意位置可能有 [react:x] → 摘掉（表情由 Chat 贴到铃的气泡上），
       剩余正文再按 [split] 切成几个小气泡（开关关 = 吞标记并回一条） */
  let quote = null;
  let segments;
  if (isUser) {
    const parsed = parseQuoted(text);
    quote = parsed.quote;
    segments = parsed.body ? [parsed.body] : [];
  } else {
    segments = splitSegments(extractReactions(text).body, splitOn);
  }
  // 纯表情回应（正文摘完为空、也没图、也没旅程卡、也没音乐卡）：表情已经贴在铃的气泡上，这行不占地方
  if (segments.length === 0 && images.length === 0 && !journey && !music && !voice) return null;
  const bubbles = segments.length > 0 ? segments : [''];
  const last = bubbles.length - 1;

  // 云旅行明信片（2026-08-31）：整条消息走全宽、无头像、旁白在卡片上方——照原作者聊天页样式。
  // 不套气泡、不缩进，卡片几乎顶到两边。
  if (!isUser && journey) {
    const lead = segments.join('\n\n');
    return (
      <div className="ch-msg ch-msg-assistant ch-msg-journey">
        <div className="ch-journey-msg">
          {showThinking && m.thinking && <ThinkingBox text={m.thinking} />}
          {lead && <div className="ch-journey-lead">{renderMessageBody(lead)}</div>}
          <JourneyCard journey={journey} onOpen={onJourneyOpen} />
          <div className="ch-msg-tools ch-msg-tools-journey">
            <button className="ch-msg-tool" onClick={onDelete} title="删除这条" aria-label="删除这条">
              <Trash2 size={12} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 他的语音条（2026-10-08）：保留头像+正常一列布局，和音乐卡同款
  if (!isUser && voice) {
    const lead = segments.join('\n\n');
    return (
      <div className="ch-msg ch-msg-assistant ch-msg-voice">
        <div className="ch-msg-line">
          <Avatar who="sael" className="ch-avatar" />
          <div className="ch-msg-col">
            {lead && <div className="ch-music-lead">{renderMessageBody(lead)}</div>}
            <VoiceBubble voice={voice} />
            <div className="ch-msg-tools">
              <button className="ch-msg-tool" onClick={onDelete} title="删除这条" aria-label="删除这条">
                <Trash2 size={12} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 聊天里的音乐卡（2026-09-03）：克克递来的一首歌，封面+歌名+歌手，点开进播放器。
  // 小卡片，保留头像+正常一列布局（不像明信片那样铺满全宽）。
  if (!isUser && music) {
    const lead = segments.join('\n\n');
    return (
      <div className="ch-msg ch-msg-assistant ch-msg-music">
        {showThinking && m.thinking && (
          <div className="ch-msg-line ch-msg-line-head">
            <Avatar who="sael" className="ch-avatar ch-avatar-ghost" />
            <div className="ch-msg-col">
              <ThinkingBox text={m.thinking} />
            </div>
          </div>
        )}
        <div className="ch-msg-line">
          <Avatar who="sael" className="ch-avatar" />
          <div className="ch-msg-col">
            {lead && <div className="ch-music-lead">{renderMessageBody(lead)}</div>}
            <MusicCard music={music} />
            <div className="ch-msg-tools">
              <button className="ch-msg-tool" onClick={onDelete} title="删除这条" aria-label="删除这条">
                <Trash2 size={12} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 单个气泡的内容（引用/图片/正文+时间/表情/工具）——两边共用。
  // 2026-09-04 铃点单：克克 [split] 拆出的每个气泡都各自成一行、各自带头像（见下方
  // assistant 分支），所以把气泡内容抽出来复用。
  const renderBubble = (seg, bi) => (
      <div className={'ch-bubble' + (bubbles.length > 1 ? ' ch-bubble-seg' : '')} key={bi}>
        {bi === 0 && quote && (
          <div className="ch-quote-chip" title="她引用的那句">
            <span className="ch-quote-from">{quote.from}</span>
            <span className="ch-quote-text">{quote.text}</span>
          </div>
        )}
        {bi === 0 && images.length > 0 && (
          <div className="ch-msg-images">
            {images.map((img, i) => (
              <img
                key={i}
                src={`data:${img.media_type};base64,${img.data}`}
                alt="附图"
                className="ch-msg-image"
                onClick={(e) => onImageView?.(img, e.currentTarget.getBoundingClientRect())}
              />
            ))}
          </div>
        )}
        {seg && (
          <div className="ch-msg-content">
            {renderMessageBody(
              seg,
              // 2026-09-04 铃点单：[split] 拆出的每个气泡都在右下角带时间
              // （原来只有最后一个有——因为底层是同一条消息、同一个时间戳）。
              m.created_at ? (
                <span className="ch-inbubble-time">{fmtTime(m.created_at)}</span>
              ) : null
            )}
          </div>
        )}
        {bi === last && Array.isArray(reactions) && reactions.length > 0 && (
          <span className="ch-react-badge" title="Sael贴的小心情">
            {reactions.join('')}
          </span>
        )}
        {bi === last && (
        <div className="ch-msg-tools">
          {onQuote && (
            <button
              className="ch-msg-tool"
              onClick={() =>
                onQuote({ id: m.id, role: m.role, text: isUser ? bubbles.join(' ') : segments.join(' ') })
              }
              disabled={generating}
              title="引用这句回复"
              aria-label="引用这句"
            >
              <Quote size={11} strokeWidth={1.6} />
            </button>
          )}
          <button
            className={'ch-msg-tool' + (copied ? ' ch-msg-tool-done' : '')}
            // 别抢走输入框焦点：她正在打字时点复制，键盘不要收起来（同 Chat.jsx 输入胶囊按钮的做法）
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const t = isUser ? bubbles.join('\n') : segments.join('\n');
              copyText(t).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              });
            }}
            title={copied ? '已复制' : '复制这句'}
            aria-label="复制这句"
          >
            {copied ? (
              <Check size={12} strokeWidth={2} />
            ) : (
              <Copy size={11} strokeWidth={1.6} />
            )}
          </button>
          {!isUser && (
            <button
              className="ch-msg-tool"
              onClick={onRegenerate}
              disabled={generating}
              title="重新生成"
              aria-label="重新生成"
            >
              <RotateCcw size={12} strokeWidth={1.6} />
            </button>
          )}
          <button
            className="ch-msg-tool"
            onClick={onDelete}
            title="删除这条"
            aria-label="删除这条"
          >
            <Trash2 size={12} strokeWidth={1.6} />
          </button>
        </div>
        )}
        {/* 图片-only 气泡（无正文段落可依附）时的兜底：时间仍单独一行右对齐贴底。
            有正文时时间已由 renderMessageBody 塞进最后段落里 float 到行尾（TG 同款）。 */}
        {bi === last && !seg && m.created_at && (
          <span className="ch-inbubble-time ch-inbubble-time-block">
            {fmtTime(m.created_at)}
          </span>
        )}
      </div>
  );

  // 她的消息：不拆分，一条=一个气泡，右侧一个头像（保持原样）。
  // 她按住说话发的语音（2026-10-08）：右边她的气泡色，点听、长按转文字
  if (isUser && voice) {
    return (
      <div className={`ch-msg ch-msg-user ch-msg-voice-mine${justSent ? ' ch-msg-sent' : ''}`}>
        <div className="ch-msg-col">
          <VoiceBubble voice={voice} mine />
          <div className="ch-msg-tools">
            <button className="ch-msg-tool" onClick={onDelete} title="删除这条" aria-label="删除这条">
              <Trash2 size={12} strokeWidth={1.6} />
            </button>
          </div>
        </div>
        <Avatar who="user" className="ch-avatar ch-avatar-user" />
      </div>
    );
  }

  if (isUser) {
    return (
      <div className={`ch-msg ch-msg-user${justSent ? ' ch-msg-sent' : ''}`}>
        <div className="ch-msg-col">
          {bubbles.map((seg, bi) => renderBubble(seg, bi))}
        </div>
        <Avatar who="user" className="ch-avatar ch-avatar-user" />
      </div>
    );
  }

  // 克克的消息：思考链单独一行（隐形头像占位对齐），然后每个气泡各自成一行、各自带头像。
  const hasHead = (showThinking && m.thinking) || (Array.isArray(m.toolTrail) && m.toolTrail.length > 0);
  return (
    <div className="ch-msg ch-msg-assistant">
      {hasHead && (
        <div className="ch-msg-line ch-msg-line-head">
          <Avatar who="sael" className="ch-avatar ch-avatar-ghost" />
          <div className="ch-msg-col">
            {showThinking && m.thinking && <ThinkingBox text={m.thinking} />}
            {Array.isArray(m.toolTrail) && m.toolTrail.length > 0 && (
              <div className="ch-tool-trail ch-tool-trail-done">
                {m.toolTrail.map((t, i) => (
                  <ToolCard key={i} t={t} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {bubbles.map((seg, bi) => (
        <div className="ch-msg-line" key={bi}>
          <Avatar who="sael" className="ch-avatar" />
          <div className="ch-msg-col">{renderBubble(seg, bi)}</div>
        </div>
      ))}
    </div>
  );
}
